import { useEffect, useState, useMemo, useCallback, useRef } from "react";
import {
  Container, Typography, Button, IconButton, TextField, Box, Autocomplete,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  Chip, Switch, CircularProgress, Tooltip, Select, MenuItem,
  Checkbox, TablePagination
} from '@mui/material';
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
} from "@tanstack/react-table";
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import LocationOffIcon from '@mui/icons-material/LocationOff';
import DriveFolderUploadIcon from '@mui/icons-material/DriveFolderUpload';
import PackageDialog from '../../components/packages/PackageDialog';
import { getAllPackages, getSinglePackages, verifyPackage, basicVerifyPackage, updatePackageRanking, deletePackage, getAvailableLocations, getAvailableTypes } from '../../api/packageAPI';
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import ClearIcon from '@mui/icons-material/Clear';
import { useDispatch, useSelector } from "react-redux";
import { removeSelectedPackage, setSelectedPackage } from "../../reduxcomponents/slices/packagesSlice";
import usePermissions from "../../hooks/UsePermissions";
import PaymentsIcon from '@mui/icons-material/Payments';
import CostDialogbox from "../../components/packages/costDialogbox";
// --------- helpers ----------
const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' });
  } catch {
    return iso;
  }
};

const safeCost = (pkg) => {
  if (pkg?.type === 'Trek') {
    return pkg?.details?.cost?.singleCost ?? "N/A";
  }
  const mc = pkg?.details?.cost?.multipleCost;
  if (Array.isArray(mc) && mc.length) {
    const first = mc[0];
    if (first?.Budget != null) return first.Budget;
    if (first?.budget != null) return first.budget;
    const anyPrice = Object.values(first || {}).find(v => typeof v === 'number' || typeof v === 'string');
    return anyPrice ?? "N/A";
  }
  return "N/A";
};

const safeDuration = (pkg) => pkg?.details?.header?.h2 ?? "—";

// ========== Component ==========
const AllPackages = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { locationSegment, searchSegment } = useParams();
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch();
  const getPermission = usePermissions();
  const lastEditedPackageId = useSelector(s => s.package.lastEditedPackageId);

  const reservedSegments = useMemo(() => ['view', 'createandedit', 'edit'], []);
  const safeLocationSegment = useMemo(() => {
    return (locationSegment && !reservedSegments.includes(locationSegment.toLowerCase()))
      ? decodeURIComponent(locationSegment)
      : "";
  }, [locationSegment, reservedSegments]);
  const safeSearchSegment = useMemo(() => {
    return searchSegment ? decodeURIComponent(searchSegment) : "";
  }, [searchSegment]);

  // Read URL search params or route segment params
  const initialLocation = searchParams.get("location") || searchParams.get("zone") || safeLocationSegment || null;
  const initialSearch = searchParams.get("search") || searchParams.get("name") || searchParams.get("q") || safeSearchSegment || "";
  const initialType = searchParams.get("type") || null;
  const initialVerified = searchParams.get("verified") || 'All';
  const initialBasicVerify = searchParams.get("basic_verify") || searchParams.get("basicVerify") || 'All';
  const initialRanking = searchParams.get("ranking") === "true";
  const initialPage = Math.max(0, (parseInt(searchParams.get("page"), 10) || 1) - 1);
  const initialLimit = parseInt(searchParams.get("limit") || searchParams.get("rowsPerPage"), 10) || 20;

  const [allPackages, setAllPackages] = useState([]);
  const [filteredPackages, setFilteredPackages] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newD, setNewD] = useState(false);
  const [singleRowData, setSingleRowData] = useState({});
  const [filterLocation, setFilterLocation] = useState(initialLocation);
  const [filterType, setFilterType] = useState(initialType);
  const [searchLabel, setSearchLabel] = useState(initialSearch);
  const [page, setPage] = useState(initialPage);
  const [rowsPerPage, setRowsPerPage] = useState(initialLimit);
  const [totalCount, setTotalCount] = useState(0);
  const [uniqueLocations, setUniqueLocations] = useState([]);
  const [uniqueTypes, setUniqueTypes] = useState([]);

  // NEW: filters
  const [filterVerified, setFilterVerified] = useState(initialVerified); // 'All' | 'Verified' | 'Not Verified'
  const [filterBasicVerify, setFilterBasicVerify] = useState(initialBasicVerify); // 'All' | 'Verified' | 'Not Verified'
  const [filterDateField, setFilterDateField] = useState('created'); // 'created' | 'updated'
  const [filterDateFrom, setFilterDateFrom] = useState(''); // YYYY-MM-DD
  const [filterDateTo, setFilterDateTo] = useState('');     // YYYY-MM-DD

  const [rankingFilter, setRankingFilter] = useState(initialRanking);

  // track which rows are updating "verified"
  const [toggleLoading, setToggleLoading] = useState({});
  const [toggleBasicLoading, setToggleBasicLoading] = useState({});

  // NEW: track which rows are updating "ranking"
  const [rankingLoading, setRankingLoading] = useState({});
  const [openDialog, setOpenDialog] = useState(false);
  const [costDialogdata, setCostDialogData] = useState({
    singleCost: null,
    multipleCost: [],
    daysCost: [],
    valueCost: [],
  });
  const [openConfirm, setOpenConfirm] = useState({ isVisible: false, section: "", target: "" });

  const handleOpenConfirm = (section, target) => {
    setOpenConfirm({ isVisible: true, section, target });
  };

  const handleCloseConfirm = () => setOpenConfirm({ isVisible: false, section: "", target: "" });

  const handleConfirmDelete = () => {
    const { section, target } = openConfirm;

    const updated = { ...costDialogdata };
    if (section === "multiple") {
      updated.multipleCost = updated.multipleCost.filter((d) => d.Persons !== target);
    } else if (section === "days") {
      updated.daysCost = updated.daysCost.filter((d) => d.Days !== target);
    } else if (section === "value") {
      updated.valueCost = updated.valueCost.filter((d) => d.Type !== target);
    }
    setCostDialogData(updated);
    handleCloseConfirm();
  };

  // Sync state if URL changes externally (e.g. Back/Forward navigation, dashboard links)
  useEffect(() => {
    const safeLoc = (locationSegment && !reservedSegments.includes(locationSegment.toLowerCase()))
      ? decodeURIComponent(locationSegment)
      : "";
    const safeSearch = searchSegment ? decodeURIComponent(searchSegment) : "";

    const urlLocation = searchParams.get("location") || searchParams.get("zone") || safeLoc || null;
    const urlSearch = searchParams.get("search") || searchParams.get("name") || searchParams.get("q") || safeSearch || "";
    const urlType = searchParams.get("type") || null;
    const urlVerified = searchParams.get("verified") || 'All';
    const urlBasicVerify = searchParams.get("basic_verify") || searchParams.get("basicVerify") || 'All';
    const urlRanking = searchParams.get("ranking") === "true";
    const rawPage = parseInt(searchParams.get("page"), 10);
    const urlPage = rawPage > 0 ? rawPage - 1 : 0;
    const urlLimit = parseInt(searchParams.get("limit") || searchParams.get("rowsPerPage"), 10) || 20;

    setFilterLocation((prev) => (prev !== urlLocation ? urlLocation : prev));
    setSearchLabel((prev) => (prev !== urlSearch ? urlSearch : prev));
    setFilterType((prev) => (prev !== urlType ? urlType : prev));
    setFilterVerified((prev) => (prev !== urlVerified ? urlVerified : prev));
    setFilterBasicVerify((prev) => (prev !== urlBasicVerify ? urlBasicVerify : prev));
    setRankingFilter((prev) => (prev !== urlRanking ? urlRanking : prev));
    setPage((prev) => (prev !== urlPage ? urlPage : prev));
    setRowsPerPage((prev) => (prev !== urlLimit ? urlLimit : prev));
  }, [searchParams, locationSegment, searchSegment, reservedSegments]);

  // Match case-insensitively once uniqueLocations / uniqueTypes load
  useEffect(() => {
    if (filterLocation && uniqueLocations.length > 0) {
      const match = uniqueLocations.find(loc => loc.toLowerCase() === filterLocation.toLowerCase());
      if (match && match !== filterLocation) {
        setFilterLocation(match);
      }
    }
  }, [uniqueLocations, filterLocation]);

  useEffect(() => {
    if (filterType && uniqueTypes.length > 0) {
      const match = uniqueTypes.find(t => t.toLowerCase() === filterType.toLowerCase());
      if (match && match !== filterType) {
        setFilterType(match);
      }
    }
  }, [uniqueTypes, filterType]);

  // Reset page to 0 only when filters actually change
  const prevFilterValuesRef = useRef({
    filterLocation,
    filterType,
    searchLabel,
    filterVerified,
    filterBasicVerify,
    rankingFilter,
  });

  useEffect(() => {
    const prev = prevFilterValuesRef.current;
    if (
      prev.filterLocation !== filterLocation ||
      prev.filterType !== filterType ||
      prev.searchLabel !== searchLabel ||
      prev.filterVerified !== filterVerified ||
      prev.filterBasicVerify !== filterBasicVerify ||
      prev.rankingFilter !== rankingFilter
    ) {
      setPage(0);
      prevFilterValuesRef.current = {
        filterLocation,
        filterType,
        searchLabel,
        filterVerified,
        filterBasicVerify,
        rankingFilter,
      };
    }
  }, [filterLocation, filterType, searchLabel, filterVerified, filterBasicVerify, rankingFilter]);

  // Keep URL in sync with state changes (debounced for search)
  const isFirstMount = useRef(true);
  useEffect(() => {
    if (isFirstMount.current) {
      isFirstMount.current = false;
      return;
    }

    const timer = setTimeout(() => {
      const basePath = filterLocation
        ? `/packages/${encodeURIComponent(filterLocation)}`
        : `/packages`;

      const nextParams = new URLSearchParams();
      if (searchLabel) nextParams.set("search", searchLabel);
      if (filterType) nextParams.set("type", filterType);
      if (filterVerified && filterVerified !== 'All') nextParams.set("verified", filterVerified);
      if (filterBasicVerify && filterBasicVerify !== 'All') nextParams.set("basic_verify", filterBasicVerify);
      if (rankingFilter) nextParams.set("ranking", "true");
      if (page > 0) nextParams.set("page", String(page + 1));
      if (rowsPerPage !== 20) nextParams.set("limit", String(rowsPerPage));

      const nextQuery = nextParams.toString();
      const targetUrl = `${basePath}${nextQuery ? `?${nextQuery}` : ""}`;
      const currentUrl = `${location.pathname}${location.search}`;

      if (targetUrl !== currentUrl) {
        navigate(targetUrl, { replace: true });
      }
    }, 250);

    return () => clearTimeout(timer);
  }, [
    filterLocation,
    filterType,
    searchLabel,
    filterVerified,
    filterBasicVerify,
    rankingFilter,
    page,
    rowsPerPage,
    navigate,
    location.pathname,
    location.search
  ]);

  // initial fetch for dropdowns
  useEffect(() => {
    getAvailableLocations().then(res => {
      setUniqueLocations(res.data.map(item => item.name).filter(Boolean));
    }).catch(console.error);

    getAvailableTypes().then(res => {
      setUniqueTypes(res.data.map(item => item.name).filter(Boolean));
    }).catch(console.error);
  }, []);

  // filtering + mapping to rows
  useEffect(() => {
    const params = {
      page: page + 1,
      limit: rowsPerPage,
      location: filterLocation || "",
      type: filterType || "",
      search: searchLabel || "",
      verified: filterVerified === 'All' ? "" : filterVerified,
      basic_verify: filterBasicVerify === 'All' ? "" : filterBasicVerify,
      rankingSort: rankingFilter ? "true" : ""
    };

    getAllPackages(params)
      .then((res) => {
        const data = res.data?.data || res.data || [];
        setAllPackages(data);
        setTotalCount(res.data?.total || data.length);
      })
      .catch((err) => {
        console.error('Failed to fetch packages', err);
      });
  }, [
    page,
    rowsPerPage,
    filterLocation,
    filterType,
    searchLabel,
    filterVerified,
    filterBasicVerify,
    rankingFilter
  ]);

  useEffect(() => {
    const labelCounts = {};
    allPackages.forEach(p => {
      const l = (p.label || "").trim().toLowerCase();
      if (l) {
        labelCounts[l] = (labelCounts[l] || 0) + 1;
      }
    });

    const rows = allPackages.map(pkg => {
      const l = (pkg.label || "").trim().toLowerCase();
      const isDuplicate = l ? (labelCounts[l] > 1) : false;
      return {
        raw: pkg,
        id: pkg._id,
        title: pkg.label || "Untitled",
        location: pkg.location || "—",
        type: pkg.type || "—",
        verified: Boolean(pkg.verified),
        basic_verify: Boolean(pkg.basic_verify),
        created_at: pkg.created_at,
        updated_at: pkg.updated_at,
        updatedBy: pkg.updatedBy || "—",
        duration: safeDuration(pkg),
        cost: safeCost(pkg),
        ranking: Number.isFinite(pkg?.ranking) ? pkg.ranking : 0,
        isDuplicate,
      };
    });

    setFilteredPackages(rows);
  }, [allPackages]);

  // actions
  const handleView = useCallback((id) => {
    navigate(`/packages/view/${id}`);
  }, [navigate]);

  const handleEdit = useCallback(async (id) => {
    try {
      const singleData = await getSinglePackages(id);
      dispatch(setSelectedPackage(singleData.data));
      navigate(`/packages/createandedit`);
    } catch (error) {
      console.error('Failed to fetch single package', error);
    }
  }, [dispatch, navigate]);

  const createNewPackage = () => {
    dispatch(removeSelectedPackage());
    navigate(`/packages/createandedit`);
  };

  const handleDelete = useCallback(async (id) => {
    if (window.confirm("Are you sure you want to delete this package?")) {
      try {
        await deletePackage(id);
        setAllPackages(prev => prev.filter(p => p._id !== id));
      } catch (err) {
        console.error('Failed to delete package', err);
        alert('Failed to delete package');
      }
    }
  }, []);

  const handleToggleVerified = useCallback(async (row, nextVal) => {
    const id = row.id;
    setToggleLoading(prev => ({ ...prev, [id]: true }));

    // optimistic UI
    setAllPackages(prev =>
      prev.map(p => (p._id === id ? { ...p, verified: nextVal } : p))
    );

    try {
      const objD = { verified: nextVal };
      await verifyPackage(id, objD); // <-- backend update
      // reflect updated_at immediately
      setAllPackages(prev =>
        prev.map(p => (p._id === id ? { ...p, updated_at: new Date().toISOString() } : p))
      );
    } catch (err) {
      console.error("Failed to update verified", err);
      // revert on error
      setAllPackages(prev =>
        prev.map(p => (p._id === id ? { ...p, verified: !nextVal } : p))
      );
    } finally {
      setToggleLoading(prev => ({ ...prev, [id]: false }));
    }
  }, []);

  const handleToggleBasicVerified = useCallback(async (row, nextVal) => {
    const id = row.id;
    setToggleBasicLoading(prev => ({ ...prev, [id]: true }));

    // optimistic UI
    setAllPackages(prev =>
      prev.map(p => (p._id === id ? { ...p, basic_verify: nextVal } : p))
    );

    try {
      const objD = { basic_verify: nextVal };
      await basicVerifyPackage(id, objD);
      setAllPackages(prev =>
        prev.map(p => (p._id === id ? { ...p, updated_at: new Date().toISOString() } : p))
      );
    } catch (err) {
      console.error("Failed to update basic verify", err);
      // revert on error
      setAllPackages(prev =>
        prev.map(p => (p._id === id ? { ...p, basic_verify: !nextVal } : p))
      );
    } finally {
      setToggleBasicLoading(prev => ({ ...prev, [id]: false }));
    }
  }, []);

  // NEW: change ranking
  const handleChangeRanking = useCallback(async (row, nextVal) => {
    const id = row.id;
    const newRanking = Number(nextVal);

    setRankingLoading(prev => ({ ...prev, [id]: true }));

    // optimistic UI
    const prevSnapshot = allPackages;
    setAllPackages(prev =>
      prev.map(p => (p._id === id ? { ...p, ranking: newRanking } : p))
    );

    try {
      // Assume API signature: updatePackageRanking(id, { ranking: number })
      await updatePackageRanking(id, { ranking: newRanking });
      // bump updated_at
      setAllPackages(prev =>
        prev.map(p => (p._id === id ? { ...p, updated_at: new Date().toISOString() } : p))
      );
    } catch (err) {
      console.error("Failed to update ranking", err);
      // revert on error
      setAllPackages(prevSnapshot);
    } finally {
      setRankingLoading(prev => ({ ...prev, [id]: false }));
    }
  }, [allPackages]);



  const handleImageUpload = useCallback((id) => {
    navigate(`/upload/package/${id}`);
  }, [navigate]);


  const label = { inputProps: { 'aria-label': 'Checkbox demo' } };


  const handleOpenDialog = useCallback((id) => {
    const currentUser = filteredPackages.find((user) => user.id === id);

    const costData = {
      singleCost: currentUser.raw.details.cost.singleCost,
      multipleCost: currentUser.raw.details.cost.multipleCost.map((cost) => ({
        Persons: cost.pax,
        Pricing: cost.pricing.map((price) => ({
          Category: price.catagory,
          Price: price.price,
        }))
      })),
      daysCost: currentUser.raw.details.cost.daysCost.map((days) => ({
        Days: days.days,
        Pricing: days.pricing.map((dat) => ({
          Category: dat.catagory,
          Price: dat.price,
        }))
      })),
      valueCost: currentUser.raw.details.cost.valueCost.map((val) => ({
        Type: val.type,
        Price: val.price,
      })),

    }

    setCostDialogData(costData);
    setOpenDialog(true);
  }, [filteredPackages]);

  const handleCloseDialog = useCallback((id) => {
    setOpenDialog(false);
  }, []);

  // Column definitions for @tanstack/react-table (must be after all handler functions)
  const columns = useMemo(() => [
    {
      accessorKey: "title",
      header: "Title",
    },
    {
      accessorKey: "location",
      header: "Location",
    },
    {
      accessorKey: "type",
      header: "Type",
    },
    {
      accessorKey: "duration",
      header: "Duration",
    },
    {
      accessorKey: "updatedBy",
      header: "Updated By",
    },
    {
      accessorKey: "basic_verify",
      header: "Basic Verify",
      cell: ({ row }) => {
        const rowData = row.original;
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {(getPermission('packages', 'alter-verify') || getPermission('packages', 'basic-verify')) &&
              <Switch
                size="small"
                checked={rowData.basic_verify}
                onChange={(e) => handleToggleBasicVerified(rowData, e.target.checked)}
                disabled={toggleBasicLoading[rowData.id]}
                inputProps={{ 'aria-label': 'Toggle basic verified' }}
              />}
            {toggleBasicLoading[rowData.id] ? (
              <CircularProgress size={16} />
            ) : (
              <Chip
                size="small"
                label={rowData.basic_verify ? "Yes" : "No"}
                color={rowData.basic_verify ? "success" : "default"}
                variant={rowData.basic_verify ? "filled" : "outlined"}
              />
            )}
          </Box>
        );
      },
    },
    {
      accessorKey: "verified",
      header: "Verified",
      cell: ({ row }) => {
        const rowData = row.original;
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {getPermission('packages', 'alter-verify') &&
              <Switch
                size="small"
                checked={rowData.verified}
                onChange={(e) => handleToggleVerified(rowData, e.target.checked)}
                disabled={toggleLoading[rowData.id]}
                inputProps={{ 'aria-label': 'Toggle verified' }}
              />}
            {toggleLoading[rowData.id] ? (
              <CircularProgress size={16} />
            ) : (
              <Chip
                size="small"
                label={rowData.verified ? "Verified" : "Not Verified"}
                color={rowData.verified ? "success" : "default"}
                variant={rowData.verified ? "filled" : "outlined"}
              />
            )}
          </Box>
        );
      },
    },
    {
      accessorKey: "ranking",
      header: "Ranking",
      cell: ({ row }) => {
        const rowData = row.original;
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {getPermission('packages', 'alter-ranking') ? (
              <Select
                size="small"
                value={rowData.ranking ?? 0}
                onChange={(e) => handleChangeRanking(rowData, e.target.value)}
                disabled={rankingLoading[rowData.id]}
                sx={{ minWidth: 80 }}
              >
                {Array.from({ length: 11 }).map((_, i) => (
                  <MenuItem key={i} value={i}>
                    {i}
                  </MenuItem>
                ))}
              </Select>
            ) : (
              <Typography>{rowData.ranking ?? 0}</Typography>
            )}
            {rankingLoading[rowData.id] && <CircularProgress size={16} />}
          </Box>
        );
      },
    },
    // {
    //   accessorKey: "created_at",
    //   header: "Created At",
    //   cell: ({ getValue }) => {
    //     const value = getValue();
    //     return (
    //       <Tooltip title={value || ""}>
    //         <span>{formatDate(value)}</span>
    //       </Tooltip>
    //     );
    //   },
    // },
    // {
    //   accessorKey: "updated_at",
    //   header: "Updated At",
    //   cell: ({ getValue }) => {
    //     const value = getValue();
    //     return (
    //       <Tooltip title={value || ""}>
    //         <span>{formatDate(value)}</span>
    //       </Tooltip>
    //     );
    //   },
    // },
    // {
    //   id: "cost",
    //   header: "Cost",
    //   cell: ({ row }) => {
    //     const rowData = row.original;
    //     return (
    //       <PaymentsIcon
    //         color="primary"
    //         onClick={() => handleOpenDialog(rowData.id)}
    //         sx={{ cursor: 'pointer' }}
    //       />
    //     );
    //   },
    // },
    {
      id: "actions",
      header: "Action",
      cell: ({ row }) => {
        const rowData = row.original;
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
            {getPermission('packages', 'alter-image') &&
              <Tooltip title="Image Upload">
                <IconButton color="success" onClick={() => handleImageUpload(rowData.id)}>
                  <DriveFolderUploadIcon />
                </IconButton>
              </Tooltip>}
            {getPermission('packages', 'view') &&
              <Tooltip title="FullView">
                <IconButton color="warning" onClick={() => handleView(rowData.id)}>
                  <VisibilityIcon />
                </IconButton>
              </Tooltip>}
            {getPermission('packages', 'alter') &&
              <Tooltip title="Edit">
                <IconButton color="primary" onClick={() => handleEdit(rowData.id)}>
                  <EditIcon />
                </IconButton>
              </Tooltip>}
            {getPermission('packages', 'delete') &&
              <Tooltip title="Delete">
                <IconButton color="error" onClick={() => handleDelete(rowData.id)}>
                  <DeleteIcon />
                </IconButton>
              </Tooltip>}
            <Tooltip title="Location">
              <IconButton color="error">
                <LocationOffIcon />
              </IconButton>
            </Tooltip>
          </Box>
        );
      },
    },
  ], [getPermission, toggleLoading, toggleBasicLoading, rankingLoading, handleToggleVerified, handleToggleBasicVerified, handleChangeRanking, handleImageUpload, handleView, handleEdit, handleDelete, handleOpenDialog]);

  // Table instance
  const table = useReactTable({
    data: filteredPackages,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <Container maxWidth={false} sx={{ py: 2 }}>
      <Typography variant="h6" sx={{ mb: 2 }}>All Packages</Typography>

      {/* Filters */}
      <Box sx={{ display: 'flex', gap: 2, my: 2, flexWrap: 'wrap' }}>
        <TextField
          label="Search Title"
          size="small"
          value={searchLabel}
          onChange={(e) => setSearchLabel(e.target.value)}
          InputProps={{
            endAdornment: searchLabel ? (
              <IconButton
                size="small"
                onClick={() => setSearchLabel("")}
                sx={{ p: 0.5 }}
              >
                <ClearIcon fontSize="small" />
              </IconButton>
            ) : null,
          }}
          sx={{ width: 250 }}
        />
        <Autocomplete
          value={filterLocation}
          onChange={(e, newVal) => {
            setFilterLocation(newVal);
            if (!newVal) setRankingFilter(false);
          }}
          options={uniqueLocations}
          renderInput={(params) => <TextField {...params} label="Location" size="small" />}
          sx={{ width: 200 }}
        />
        <Autocomplete
          value={filterType}
          onChange={(e, newVal) => setFilterType(newVal)}
          options={uniqueTypes}
          renderInput={(params) => <TextField {...params} label="Type" size="small" />}
          sx={{ width: 200 }}
        />

        {/* Verified filter */}
        <TextField
          label="Verified"
          size="small"
          select
          value={filterVerified}
          onChange={(e) => setFilterVerified(e.target.value)}
          sx={{ width: 160 }}
          SelectProps={{ native: true }}
        >
          <option value="All">All</option>
          <option value="Verified">Verified</option>
          <option value="Not Verified">Not Verified</option>
        </TextField>

        {/* Basic Verify filter */}
        <TextField
          label="Basic Verify"
          size="small"
          select
          value={filterBasicVerify}
          onChange={(e) => setFilterBasicVerify(e.target.value)}
          sx={{ width: 160 }}
          SelectProps={{ native: true }}
        >
          <option value="All">All</option>
          <option value="Verified">Verified</option>
          <option value="Not Verified">Not Verified</option>
        </TextField>

        {/* NEW: Date field selector */}
        {/* <TextField
          label="Date Field"
          size="small"
          select
          value={filterDateField}
          onChange={(e) => setFilterDateField(e.target.value)}
          sx={{ width: 160 }}
          SelectProps={{ native: true }}
        >
          <option value="created">Created At</option>
          <option value="updated">Updated At</option>
        </TextField> */}

        {/* NEW: From / To dates */}
        {/* <TextField
          label="From"
          size="small"
          type="date"
          value={filterDateFrom}
          onChange={(e) => setFilterDateFrom(e.target.value)}
          sx={{ width: 170 }}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          label="To"
          size="small"
          type="date"
          value={filterDateTo}
          onChange={(e) => setFilterDateTo(e.target.value)}
          sx={{ width: 170 }}
          InputLabelProps={{ shrink: true }}
        /> */}
        {getPermission('packages', 'create') &&
          <Button
            variant="contained"
            size="small"
            color="primary"
            onClick={createNewPackage}
          >
            Add New Package
          </Button>
        }

        <Typography
          color={filterLocation ? "textPrimary" : "text.disabled"}
          display="flex"
          alignItems="center"
          gap={1}
        >
          Ranking
          <Checkbox
            {...label}
            disabled={!filterLocation}
            checked={rankingFilter}
            onChange={(e) => setRankingFilter(e.target.checked)}
          />
        </Typography>

      </Box>

      {/* Table */}
      <Paper sx={{ overflow: "hidden" }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              {table.getHeaderGroups().map(headerGroup => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map(header => (
                    <TableCell
                      key={header.id}
                      align={header.id === "actions" ? "center" : "left"}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableHead>
            <TableBody>
              {table.getRowModel().rows.map(row => (
                <TableRow
                  key={row.id}
                  hover
                  sx={{
                    backgroundColor: row.original.isDuplicate
                      ? '#fff4e5'
                      : row.original.id === lastEditedPackageId
                        ? '#e8f5e9'
                        : 'inherit',
                    outline: row.original.id === lastEditedPackageId ? '2px solid #4caf50' : 'none',
                    outlineOffset: '-2px',
                  }}
                >
                  {row.getVisibleCells().map(cell => (
                    <TableCell
                      key={cell.id}
                      align={cell.column.id === "actions" ? "center" : "left"}
                      sx={cell.column.id === "actions" ? { width: "200px" } : {}}
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
              {table.getRowModel().rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={columns.length} align="center">
                    No packages found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 20, 50, 100]}
        />
      </Paper>

      {/* Dialog (Optional) */}
      <PackageDialog
        open={dialogOpen}
        singleRowData={singleRowData}
        newD={newD}
        handleClose={() => {
          setSingleRowData({});
          setNewD(false);
          setDialogOpen(false);
        }}
      />
      <CostDialogbox
        open={openDialog}
        openConfirm={openConfirm}
        handleClose={handleCloseDialog}
        costDialogdata={costDialogdata}
        setCostDialogData={setCostDialogData}
        handleOpenConfirm={handleOpenConfirm}
        handleCloseConfirm={handleCloseConfirm}
        handleConfirmDelete={handleConfirmDelete}
      />
    </Container>
  );
};

export default AllPackages;
